/* eslint-disable prettier/prettier */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs } from 'antd';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import generatePDF from 'react-to-pdf';
import { getSignedUrl } from 'store/commonSlice';
import { getListAction } from 'store/pageSlice';
import { getPodUrl } from 'store/approvalSlice';
import {
	getTripListActionV2,
	getTripMarginStatementV2,
	searchProofOfDeliveryV2,
	getPodUrlsV2,
	getConsignmentNoteV2,
	getAllTrackingInfoV2,
} from 'store/tripSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import {
	TripOverviewTabV2,
	ExpensesTab,
	PODTab,
	LROverviewTab,
	TemperatureTab,
	DocumentsTab,
	DistanceTab,
} from './components';
import { ACL } from 'components/common';
import { useAuth } from 'hooks';

const { TabPane } = Tabs;

const normalizeToArray = (value) => {
	if (Array.isArray(value)) return value;
	if (value) return [value];
	return [];
};

function TripOverviewPageV2() {
	const { tripId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { permissions, user_type } = useAuth();

	const isV2UI = getUIVersion() === 'new';

	const [activeTab, setActiveTab] = useState('trip-overview');
	const [lrDetails, setLrDetails] = useState(null);
	const [lrLoading, setLrLoading] = useState(false);
	const [lrError, setLrError] = useState(null);
	const [tripDetails, setTripDetails] = useState(null);
	const [headerTripData, setHeaderTripData] = useState(null);
	const [trackingInfo, setTrackingInfo] = useState(null);
	const [trackingLoading, setTrackingLoading] = useState(false);
	const [liveTracking, setLiveTracking] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [printVisible, setPrintVisible] = useState(false);
	const [isPDFPrint, setPDFPrint] = useState(false);
	const [selectedLRDetails, setSelectedLRDetails] = useState(null);
	const [expensesData, setExpensesData] = useState([]);
	const [expensesLoading, setExpensesLoading] = useState(false);
	const [podData, setPodData] = useState(null);
	const [podLoading, setPodLoading] = useState(false);
	const [podError, setPodError] = useState(null);
	const [blobUrl, setBlobUrl] = useState(null); // Track blob URLs for cleanup
	const [podKey, setPodKey] = useState(null);
	const [podUploadData, setPodUploadData] = useState(null);
	const LRPrintRef = useRef();
	const printComponent = useReactToPrint({
		content: () => LRPrintRef.current,
		pageStyle: '@page { size: 42cm 56cm }',
	});

	// Helper function to fetch POD data
	// This function first searches for proof of delivery data to get the trip ID,
	// then uses that ID to fetch the POD URL
	// Uses V2 or V1 APIs based on UI version
	// eslint-disable-next-line sonarjs/cognitive-complexity
	const fetchPODData = async (currentTripId) => {
		try {
			const normalizedTripId = currentTripId != null ? String(currentTripId) : '';
			// First, search for proof of delivery data to get the POD ID
			let searchRes;
			if (isV2UI) {
				searchRes = await dispatch(
					searchProofOfDeliveryV2({
						trip_id: normalizedTripId,
						limit: 10,
						offset: 0,
					})
				);
			} else {
				searchRes = await dispatch(
					getListAction({
						search_type: 'proof_of_delivery',
						trip_id: normalizedTripId,
						limit: 10,
						offset: 0,
					})
				);
			}

			const podRecord = searchRes?.rows?.[0] || searchRes?.data?.[0];
			setPodUploadData(podRecord);

			const proofOfDeliveryKeys = normalizeToArray(podRecord?.proof_of_delivery_key);
			const driverPodKeys = normalizeToArray(podRecord?.driver_pod_keys);
			const allPodKeys = [...proofOfDeliveryKeys, ...driverPodKeys].filter(Boolean);

			setPodKey(allPodKeys.join(','));

			// Find the POD data from the search results
			const foundPodData =
				searchRes?.rows?.find(
					(item) =>
						String(item.trip_id) === normalizedTripId ||
						String(item.id) === normalizedTripId
				) ||
				searchRes?.data?.find(
					(item) =>
						String(item.trip_id) === normalizedTripId ||
						String(item.id) === normalizedTripId
				);

			if (foundPodData && foundPodData.id) {
				if (isV2UI) {
					return {
						podUrlRes: await dispatch(getPodUrlsV2({ id: foundPodData.id })),
						podUploadRecord: podRecord,
					};
				}
				return {
					podUrlRes: await dispatch(getPodUrl({ id: foundPodData.id })),
					podUploadRecord: podRecord,
				};
			}
			if (isV2UI) {
				return {
					podUrlRes: await dispatch(getPodUrlsV2({ id: normalizedTripId })),
					podUploadRecord: podRecord,
				};
			}
			return {
				podUrlRes: await dispatch(getPodUrl({ id: normalizedTripId })),
				podUploadRecord: podRecord,
			};
		} catch (fetchError) {
			console.error('Error in fetchPODData:', fetchError);
			throw fetchError;
		}
	};

	const getSignedPodUrlsFromKeys = async (keys = []) => {
		if (!keys.length) return [];

		const response = await dispatch(
			getSignedUrl({
				url_type: 'view',
				data: {
					entity: 'proof_of_delivery_upload',
					keys,
				},
			})
		);

		return (response?.data || []).map((item) => item?.url).filter(Boolean);
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (tripId) {
			setLoading(true);
			const tripAction = isV2UI
				? getTripListActionV2({
						search_type: 'trip',
						id: tripId,
						limit: 1,
						offset: 0,
				  })
				: getListAction({
						search_type: 'trip',
						id: tripId,
						filterflag: false,
				  });

			dispatch(tripAction)
				.then((res) => {
					const trip = res?.rows?.[0];
					if (trip) {
						setHeaderTripData(trip);
						const projectDisplayName = isV2UI
							? trip.project_name || '-'
							: trip.info?.project_location_code || '-';
						const vendorTypeDisplay = isV2UI
							? trip.vendor_details?.vendor_type || '-'
							: trip.vendor_type || '-';
						const driverNumberDisplay = trip.info?.driver_number || '-';
						const vehicleTypeDisplay = trip.vehicle_type || '-';
						const startKmDisplay = trip.start_km ?? trip.start_odo ?? '-';
						const endKmDisplay = trip.end_km ?? trip.end_odo ?? '-';

						let reportingDateTime;
						if (isV2UI && trip.reporting_date) {
							const datePart = trip.reporting_date.split(' ')[0];
							const timePart = trip.reporting_time || '00:00:00';
							const [dd, mm, yyyy] = datePart.split('/');
							reportingDateTime = `${yyyy}-${mm}-${dd}T${timePart}`;
						} else if (trip?.info?.reporting_date && trip?.info?.reporting_time) {
							reportingDateTime = `${trip.info.reporting_date} ${trip.info.reporting_time}`;
						} else if (trip?.info?.reporting_date) {
							reportingDateTime = trip.info.reporting_date;
						} else {
							reportingDateTime = trip.reporting_time;
						}

						setTripDetails({
							is_vendor_billed: trip.is_vendor_billed,
							client: trip.client_name || '-',
							project: projectDisplayName,
							vendor: trip.vendor_name || '-',
							reportingDateTime,
							startDateTime: (() => {
								if (isV2UI && trip.actual_start_time) {
									const [datePart, timePart] = trip.actual_start_time.split(' ');
									const [dd, mm, yyyy] = datePart.split('/');
									const utcMs = new Date(
										`${yyyy}-${mm}-${dd}T${timePart}Z`
									).getTime();
									const ist = new Date(utcMs - (5 * 60 + 30) * 60 * 1000);
									const pad = (n) => String(n).padStart(2, '0');
									return `${ist.getUTCFullYear()}-${pad(
										ist.getUTCMonth() + 1
									)}-${pad(ist.getUTCDate())}T${pad(ist.getUTCHours())}:${pad(
										ist.getUTCMinutes()
									)}:${pad(ist.getUTCSeconds())}`;
								}
								return trip.start_time;
							})(),
							endDateTime: trip.end_time,
							runTime: (() => {
								const duration = parseFloat(trip.trip_duration_hours);
								if (Number.isNaN(duration)) return '-';
								const hours = Math.floor(duration);
								const minutes = Math.round((duration - hours) * 60);
								return `${hours} hrs ${minutes} mins`;
							})(),
							sourceLocation: trip.source_location || trip.source_pincode || '-',
							destinationLocation:
								trip.destination_location || trip.destination_pincode || '-',
							tripType: (() => {
								if (trip.trip_type === 'adhoc') return 'Adhoc Trip';
								if (trip.trip_type === 'regular') return 'Regular Trip';
								return trip.trip_type || 'Regular Trip';
							})(),
							invoiceNumber: trip.invoice_number || '-',
							invoiceDate: trip.invoice_date || '-',
							vendorType: vendorTypeDisplay,
							projectName: projectDisplayName,
							city: trip.city || '-',
							clientRate: trip.client_trip_cost || '-',
							vendorRate: trip.vendor_trip_cost || '-',
							billingType: trip.billing_type || null,
							clientFixedRatePerDay: trip.client_fixed_rate_per_day ?? null,
							vendorFixedRatePerDay: trip.vendor_fixed_rate_per_day ?? null,
							fixedPerDayBreakdownClient: trip.fixed_per_day_breakdown_client || null,
							fixedPerDayBreakdownVendor: trip.fixed_per_day_breakdown_vendor || null,
							driverNumber: driverNumberDisplay,
							driverName: trip.driver_name || '-',
							vehicleType: vehicleTypeDisplay,
							vehicleNumber: trip.vehicle_number || '-',
							is_cross_vendor_vehicle: trip.is_cross_vendor_vehicle || false,
							vehicle_vendor_name: trip.vehicle_vendor_name || '',
							lrId: trip.id,
							interCity:
								trip.info?.is_inter_city ||
								trip.is_inter_city ||
								trip.trip_info?.is_inter_city ||
								trip.movement_type?.toLowerCase() === 'intercity' ||
								trip.trip_type?.toLowerCase() === 'intercity' ||
								false,
							reqProof: trip.requirement_proof || false,
							endTime: trip.end_time || trip.actual_end_time,
							isEnded: isV2UI
								? trip.trip_status_id === 3 ||
								  (typeof trip.trip_status === 'string' &&
										trip.trip_status.toLowerCase() === 'trip_closed')
								: !!trip.end_time,
							startKm: startKmDisplay,
							endKm: endKmDisplay,
							mapKm: trip.map_km,
							tripStatus: trip.trip_status_name,
							tripApprovalStatus: trip.trip_approval_status,
							isApproved: trip.is_approved,
							isValid: trip.is_valid,
							podStatus: trip.pod_status,
							createdAt: trip.created_at,
							updatedAt: trip.updated_at,
							info: trip.info,
							trip_status: trip.trip_status,
							trip_documents: trip.trip_documents,
							toll_info: trip.toll_info,
							requirement_proof: trip.requirement_proof,
							por_approval_status:
								trip.por_approval_status || trip.info?.por_approval_status,
							is_temperature_enabled: trip?.is_temperature_control,
							adhocRequestId: trip.adhoc_request_id || '-',
							advanceAmount: trip.advance_amount || '-',
							tripStatusName: trip.trip_status_name || '-',
							remarks: trip.remarks || trip.info?.remarks || '',
							perOrderRates: (() => {
								const info = trip.info || {};
								const breakdown = Array.isArray(info.order_line_breakdown)
									? info.order_line_breakdown
									: [];
								if (breakdown.length) {
									return breakdown.map((r) => ({
										rate_id: r.rate_id,
										client_rate: r.client_rate,
										vendor_rate: r.vendor_rate,
										quantity: r.quantity,
									}));
								}
								const rateRows = Array.isArray(info.order_rates)
									? info.order_rates
									: trip.order_rates || [];
								const qtyRows = Array.isArray(info.order_quantities)
									? info.order_quantities
									: trip.order_quantities || [];
								const qtyById = qtyRows.reduce((acc, q) => {
									acc[q.rate_id] = q.quantity;
									return acc;
								}, {});
								return rateRows.map((r) => ({
									rate_id: r.rate_id,
									client_rate: r.client_rate,
									vendor_rate: r.vendor_rate,
									quantity: r.quantity ?? qtyById[r.rate_id] ?? 0,
								}));
							})(),
						});
					}
					setLoading(false);
				})
				.catch(() => {
					setError('Failed to fetch trip details');
					setLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, tripId, isV2UI]);

	useEffect(() => {
		let intervalId;

		if (tripId) {
			const fetchTrackingInfo = () => {
				dispatch(
					getAllTrackingInfoV2({
						search_type: 'all_tracking_info',
						trip_id: tripId,
					})
				)
					.then((res) => {
						const data = res?.data;

						setTrackingInfo(data);
						setLiveTracking(data);
						setTrackingLoading(false);
					})
					.catch((fetchError) => {
						console.error('Failed to fetch tracking info', fetchError);
						setTrackingLoading(false);
					});
			};

			setTrackingLoading(true);
			fetchTrackingInfo(); // initial fetch

			intervalId = setInterval(fetchTrackingInfo, 15000); // poll every 15 seconds
		}

		return () => {
			if (intervalId) clearInterval(intervalId);
		};
	}, [dispatch, tripId]);

	const isTemperatureEnabled = tripDetails?.is_temperature_enabled;
	useEffect(() => {
		if (activeTab === 'lr-overview' && tripDetails?.interCity && tripId) {
			setLrLoading(true);
			const lrAction = isV2UI
				? getConsignmentNoteV2({ trip_id: tripId })
				: getListAction({
						search_type: 'consignment_note',
						trip_id: tripId,
				  });

			dispatch(lrAction)
				.then((res) => {
					setLrDetails(res?.rows?.[0] || null);
					setLrLoading(false);
				})
				.catch(() => {
					setLrError('Failed to fetch LR details');
					setLrLoading(false);
				});
		}
	}, [activeTab, tripDetails, dispatch, tripId, isV2UI]);

	// Fetch expenses data when expenses tab is active
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (activeTab === 'expenses' && tripId) {
			setExpensesLoading(true);
			const expensesAction = isV2UI
				? getTripMarginStatementV2({ trip_id: tripId })
				: getListAction({
						search_type: 'trip_margin_statement',
						trip_id: tripId,
				  });

			dispatch(expensesAction)
				.then((res) => {
					const data =
						res?.data || res?.rows || res?.payload?.data || res?.payload?.rows || [];
					setExpensesData(data);
					setExpensesLoading(false);
				})
				// eslint-disable-next-line no-shadow
				.catch((error) => {
					console.error('Expenses API Error:', error);
					setExpensesLoading(false);
				});
		}
	}, [activeTab, tripId, dispatch, isV2UI]);

	// Fetch POD data when POD tab is active
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (activeTab === 'pod' && tripId) {
			setPodLoading(true);
			setPodError(null); // Clear any previous errors

			fetchPODData(tripId)
				.then(async ({ podUrlRes, podUploadRecord }) => {
					let data = null;
					if (
						isV2UI &&
						podUrlRes &&
						typeof podUrlRes === 'object' &&
						!Array.isArray(podUrlRes)
					) {
						// V2 returns the URL object directly from getPodUrlsV2
						if (podUrlRes.proof_of_delivery_url || podUrlRes.pod_document_url) {
							data = podUrlRes;
						}
					} else if (podUrlRes?.data) {
						// V1 response handling
						data = Array.isArray(podUrlRes.data) ? podUrlRes.data[0] : podUrlRes.data;
					} else if (podUrlRes?.rows) {
						data = Array.isArray(podUrlRes.rows) ? podUrlRes.rows[0] : podUrlRes.rows;
					} else if (podUrlRes?.payload?.data) {
						data = Array.isArray(podUrlRes.payload.data)
							? podUrlRes.payload.data[0]
							: podUrlRes.payload.data;
					} else if (podUrlRes?.payload?.rows) {
						data = Array.isArray(podUrlRes.payload.rows)
							? podUrlRes.payload.rows[0]
							: podUrlRes.payload.rows;
					}
					const driverPodKeys = normalizeToArray(podUploadRecord?.driver_pod_keys);
					const driverPodUrls = await getSignedPodUrlsFromKeys(driverPodKeys);
					// Check if the response contains binary data (starts with PNG, JPEG, etc.)
					if (
						data &&
						typeof data === 'string' &&
						(data.startsWith('PNG') ||
							data.startsWith('JPEG') ||
							data.startsWith('GIF'))
					) {
						// Create a blob URL from the binary data
						const blob = new Blob([data], { type: 'image/png' });
						const newBlobUrl = URL.createObjectURL(blob);
						setPodData({
							proof_of_delivery_url: newBlobUrl,
							driver_pod_url: driverPodUrls,
						});
						setBlobUrl(newBlobUrl); // Store the blob URL
					} else if (
						data &&
						(data.proof_of_delivery_url || data.url || driverPodUrls.length)
					) {
						setPodData({
							...data,
							driver_pod_url: driverPodUrls,
						});
					} else if (driverPodUrls.length) {
						setPodData({ driver_pod_url: driverPodUrls });
					} else {
						setPodError('No POD URL found for this trip');
					}
					setPodLoading(false);
				})
				.catch((podApiError) => {
					setPodError(podApiError.message || 'No pod exists for this trip');
					setPodLoading(false);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTab, tripId, dispatch, isV2UI]);

	const handleDownload = () => {
		setSelectedLRDetails(lrDetails);
		setPDFPrint(true);
		setPrintVisible(true);
	};

	const handlePrint = () => {
		setSelectedLRDetails(lrDetails);
		setPDFPrint(false);
		setPrintVisible(true);
	};

	useEffect(() => {
		if (printVisible) {
			setTimeout(() => {
				if (isPDFPrint) {
					generatePDF(LRPrintRef, {
						filename: `CSN${String(selectedLRDetails?.id).padStart(6, '0')}.pdf`,
						method: 'save',
						resolution: 1,
						page: { margin: 5, format: 'A4', orientation: 'portrait' },
						canvas: { mimeType: 'image/jpeg', qualityRatio: 1 },
						overrides: {
							pdf: { compress: true },
							canvas: { useCORS: true },
							unit: 'mm',
						},
					});
				} else {
					printComponent();
				}
				setPrintVisible(false);
				setPDFPrint(false);
			}, 200);
		}
	}, [printVisible, isPDFPrint, selectedLRDetails, printComponent]);

	useEffect(
		() => () => {
			if (blobUrl) {
				URL.revokeObjectURL(blobUrl);
			}
		},
		[blobUrl]
	);

	// Permission checks for tab visibility
	const canViewExpenses =
		(user_type !== 'supervisor' && permissions?.search?.includes('trip_margin_statement')) ||
		permissions?.search?.includes('all');
	const canViewPOD =
		permissions?.search?.includes('proof_of_delivery') || permissions?.search?.includes('all');
	const canViewLR =
		permissions?.search?.includes('consignment_note') || permissions?.search?.includes('all');
	const isPODRequired = trackingInfo?.trip_info?.pod_required;
	const resolvedHeaderTripData =
		headerTripData && trackingInfo
			? {
					...headerTripData,
					actual_end_time:
						headerTripData.actual_end_time ||
						trackingInfo?.trip_timings?.actual_end_time,
					actual_end_km:
						headerTripData.actual_end_km ??
						headerTripData.end_km ??
						trackingInfo?.trip_timings?.actual_end_km,
					trip_timings: trackingInfo?.trip_timings,
			  }
			: headerTripData;

	return (
		<div style={{ padding: 32, marginTop: '-40px' }}>
			<Tabs activeKey={activeTab} onChange={setActiveTab}>
				<TabPane tab="Trip Overview" key="trip-overview">
					<TripOverviewTabV2
						tripDetails={tripDetails}
						headerTripData={resolvedHeaderTripData}
						loading={loading}
						error={error}
						tripId={tripId}
						navigate={navigate}
						isV2={isV2UI}
						trackingInfo={trackingInfo}
						trackingLoading={trackingLoading}
						liveTracking={liveTracking}
					/>
				</TabPane>
				{/* Always available: every trip type, every trip state. It degrades, never hides. */}
				<TabPane
					tab={
						<span>
							<span>Distance</span>
							<span
								style={{
									marginLeft: 8,
									background: '#2563eb',
									color: '#fff',
									borderRadius: 999,
									padding: '1px 8px',
									fontSize: 10,
									fontWeight: 700,
									letterSpacing: '0.5px',
								}}
							>
								NEW
							</span>
						</span>
					}
					key="distance"
				>
					<DistanceTab
						tripId={tripId}
						trackingInfo={trackingInfo}
						trackingLoading={trackingLoading}
						tripDetails={tripDetails}
					/>
				</TabPane>
				{canViewExpenses && tripDetails?.isEnded && (
					<TabPane tab="Expenses" key="expenses">
						<ACL action="search" module="trip_margin_statement">
							<ExpensesTab
								tripDetails={tripDetails}
								tripId={tripId}
								expensesData={expensesData}
								expensesLoading={expensesLoading}
								perOrderRates={tripDetails?.perOrderRates}
							/>
						</ACL>
					</TabPane>
				)}
				{tripDetails?.isEnded && isTemperatureEnabled === true && (
					<TabPane tab="Temperature" key="temperature">
						<TemperatureTab tripId={tripId} />
					</TabPane>
				)}
				{canViewPOD && tripDetails?.isEnded && isPODRequired === true && (
					<TabPane tab="POD" key="pod">
						<ACL action="search" module="proof_of_delivery">
							<PODTab
								podUploadData={podUploadData}
								podData={podData}
								podLoading={podLoading}
								podError={podError}
								podKey={podKey}
								isV2
							/>
						</ACL>
					</TabPane>
				)}
				{canViewLR && tripDetails?.interCity && (
					<TabPane tab="LR Overview" key="lr-overview">
						<ACL action="search" module="consignment_note">
							<LROverviewTab
								lrDetails={lrDetails}
								lrLoading={lrLoading}
								lrError={lrError}
								handleDownload={handleDownload}
								handlePrint={handlePrint}
								tripDetails={tripDetails}
								tripId={tripId}
								navigate={navigate}
								printVisible={printVisible}
								LRPrintRef={LRPrintRef}
								selectedLRDetails={selectedLRDetails}
								isPDFPrint={isPDFPrint}
							/>
						</ACL>
					</TabPane>
				)}
				<TabPane tab="Documents" key="documents">
					<DocumentsTab tripDetails={tripDetails} loading={loading} isV2 />
				</TabPane>
			</Tabs>
		</div>
	);
}

export default TripOverviewPageV2;
